import { MessageSquare, BookOpen, GraduationCap, Play } from "lucide-react";
import { useTranslation } from "react-i18next";
import Markdown from "react-markdown";
import { Text } from "@core/index";
import { VideoAvatarResponseDto } from "@packages/api/madlenAPI.schemas";
import { CourseData } from "@packages/constant/CourseData";
import { useRef, useState } from "react";
import { AvatarLoader } from "../tool_components/AvatarLoader";

interface HeygenVideoCardProps {
  avatar: VideoAvatarResponseDto;
  onCreateAgain?: () => void;
}

const HeygenVideoCard = ({ avatar }: HeygenVideoCardProps) => {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const thumbnailUrl = avatar.imageUrl;

  const handlePlayClick = () => {
    if (videoRef.current) {
      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleVideoEnded = () => {
    setIsPlaying(false);
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
    }
  };

  return (
    <div data-testid="heygen-avatar-card" className="min-h-screen">
      <div className="container max-w-7xl mx-auto px-4 py-8">
        {/* Video Player */}
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg rounded-2xl shadow-xl p-6 border border-gray-200/50 dark:border-gray-700/50 mb-6">
          <div className="w-full overflow-hidden rounded-xl bg-gray-200 dark:bg-gray-700 aspect-video relative mb-2">
            {avatar.status === "PROCESSING" ? (
              <AvatarLoader avatarDetails={avatar.avatarSampleVideoUrl} />
            ) : avatar.status === "FAILED" ? (
              <div className="w-full h-full flex items-center justify-center bg-gray-200 dark:bg-gray-700">
                <Text variant="p" status="secondary">
                  heygenVideoGenerator.noVideoAvailable
                </Text>
              </div>
            ) : avatar.videoUrl ? (
              <>
                <video
                  ref={videoRef}
                  src={avatar.videoUrl}
                  className="w-full h-full object-cover"
                  poster={thumbnailUrl}
                  onEnded={handleVideoEnded}
                  controls={isPlaying}
                />
                {!isPlaying && thumbnailUrl && (
                  <div
                    className="absolute inset-0 flex items-center justify-center cursor-pointer"
                    onClick={handlePlayClick}
                  >
                    <div className="bg-black/30 p-5 rounded-full backdrop-blur-sm">
                      <Play size={50} className="text-white fill-white" />
                    </div>
                  </div>
                )}
              </>
            ) : thumbnailUrl ? (
              <img
                src={thumbnailUrl}
                alt={avatar.title || "Avatar"}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gray-200 dark:bg-gray-700">
                <Text variant="p" status="secondary">
                  heygenVideoGenerator.noVideoAvailable
                </Text>
              </div>
            )}
          </div>
          {/* Title and Metadata */}
          {avatar.title && (
            <Text variant="h1" className="text-center mt-4 mb-2">
              {avatar.title}
            </Text>
          )}
          {/* Additional Details */}
          {avatar.description && (
            <div className="mb-2">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 rounded-xl bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400">
                  <MessageSquare size={20} />
                </div>
                <Text
                  variant="p"
                  weight="semibold"
                  className="text-indigo-600 dark:text-indigo-400"
                >
                  common.description
                </Text>
              </div>
              <div className="prose dark:prose-invert max-w-none text-sm text-gray-500 dark:text-gray-400">
                <Markdown>{avatar.description}</Markdown>
              </div>
            </div>
          )}
          {/* Course and Grade Level Chips */}
          <div className="flex flex-wrap items-center justify-center gap-2 mb-6">
            {avatar.courseName && (
              <div className="flex items-start bg-blue-500/90 px-3 py-1.5 rounded-full max-w-full">
                <BookOpen size={14} className="text-white shrink-0 mt-1" />
                <Text
                  variant="span"
                  size="sm"
                  weight="medium"
                  className="text-white ml-2 break-words"
                >
                  {avatar.courseName &&
                  CourseData[avatar.courseName as keyof typeof CourseData]
                    ? t(
                        `courseType.${CourseData[avatar.courseName as keyof typeof CourseData].name(avatar.gradeLevel ?? 8)}`,
                      )
                    : avatar.courseName}
                </Text>
              </div>
            )}
            {avatar.gradeLevel !== null &&
              avatar.gradeLevel !== undefined &&
              avatar.gradeLevel !== 0 && (
                <div className="flex items-start bg-purple-500/90 px-3 py-1.5 rounded-full max-w-full">
                  <GraduationCap
                    size={14}
                    className="text-white shrink-0 mt-1"
                  />
                  <Text
                    variant="span"
                    size="sm"
                    weight="medium"
                    className="text-white ml-2 break-words"
                  >
                    {t("hooks.form.gradeOf", {
                      grade: avatar.gradeLevel,
                    })}
                  </Text>
                </div>
              )}
          </div>
          {/* Actions Bar */}
          {/* <div className="flex justify-end">
            <div className="flex gap-2">
              {onCreateAgain && (
                <Button
                  onClick={onCreateAgain}
                  variant="solid"
                  size="md"
                  textColor="text-white dark:text-white"
                  bgColor="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                  rounded="xl"
                >
                  <RefreshCw size={18} />
                  <span>{t("common.createAnother")}</span>
                </Button>
              )}
            </div>
          </div> */}
        </div>

        {/* Transcript */}
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg rounded-2xl shadow-xl p-6 border border-gray-200/50 dark:border-gray-700/50">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-xl bg-purple-100 dark:bg-purple-900/50 text-purple-600 dark:text-purple-400">
              <MessageSquare size={20} />
            </div>
            <Text
              variant="p"
              weight="semibold"
              className="text-purple-600 dark:text-purple-400"
            >
              heygenVideoGenerator.transcript
            </Text>
          </div>

          {avatar.transcript ? (
            <div className="prose dark:prose-invert max-w-none">
              <Markdown>{avatar.transcript}</Markdown>
            </div>
          ) : (
            <Text variant="p" status="secondary" className="italic">
              heygenVideoGenerator.noTranscriptAvailable
            </Text>
          )}
        </div>
      </div>
    </div>
  );
};

export default HeygenVideoCard;
